import type { ApiClient } from "../api";
import { friendlyMessage } from "../api";
import { formatDistance, haversineMeters } from "../distance";
import { openDirections } from "../directions";
import type { Toilet } from "../types";

export interface DetailsCtx {
  api: ApiClient;
  panelRoot: HTMLElement;
  currentLocation: () => { lat: number; lon: number } | null;
  onVisitedChange: () => void;
  notify: (msg: string) => void;
}

function classify(t: Toilet): { sourceLabel: string; isPersonal: boolean } {
  if (t.source === "osm-live") return { sourceLabel: "OSM (live)", isPersonal: false };
  if (t.source === "osm-snapshot") return { sourceLabel: "OSM (snapshot)", isPersonal: false };
  return { sourceLabel: "user pin", isPersonal: !t.is_public };
}

function refForVisit(t: Toilet): string {
  if (t.source === "user") return String(t.id);
  if (t.source === "osm-snapshot" && t.id !== undefined) return String(t.id);
  return t.osm_id;
}

export function openDetails(t: Toilet, ctx: DetailsCtx): void {
  ctx.panelRoot.innerHTML = "";

  const sheet = document.createElement("section");
  sheet.className = "panel sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Toilet details");

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "panel-close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "✕";
  closeBtn.addEventListener("click", () => {
    ctx.panelRoot.innerHTML = "";
  });
  sheet.appendChild(closeBtn);

  const { sourceLabel, isPersonal } = classify(t);

  const head = document.createElement("div");
  head.className = "panel-head";
  const headTitle = document.createElement("h2");
  headTitle.textContent = isPersonal ? "Personal pin" : sourceLabel === "user pin" ? "Public pin" : sourceLabel;
  head.appendChild(headTitle);
  const sourcePill = document.createElement("span");
  sourcePill.className = "pill";
  sourcePill.textContent = sourceLabel;
  head.appendChild(sourcePill);
  if (isPersonal) {
    const personalPill = document.createElement("span");
    personalPill.className = "pill pill-personal";
    personalPill.textContent = "personal";
    head.appendChild(personalPill);
    const reminder = document.createElement("p");
    reminder.className = "muted";
    reminder.textContent = "only you can see this";
    head.appendChild(reminder);
  }
  sheet.appendChild(head);

  const here = ctx.currentLocation();
  if (here) {
    const dist = haversineMeters(here, { lat: t.lat, lon: t.lon });
    const distP = document.createElement("p");
    distP.className = "distance";
    distP.textContent = formatDistance(dist);
    sheet.appendChild(distP);
  }

  // Source-specific metadata
  if (t.source === "user" && t.notes) {
    const notes = document.createElement("div");
    notes.className = "notes";
    notes.textContent = t.notes; // textContent is XSS-safe
    sheet.appendChild(notes);
  }
  if ((t.source === "osm-live" || t.source === "osm-snapshot") && t.tags) {
    const dl = document.createElement("dl");
    dl.className = "tags";
    const interesting = ["fee", "access", "opening_hours", "wheelchair", "fee:amount"] as const;
    for (const k of interesting) {
      const val = t.tags[k];
      if (val) {
        const dt = document.createElement("dt");
        dt.textContent = k;
        const dd = document.createElement("dd");
        dd.textContent = val;
        dl.appendChild(dt);
        dl.appendChild(dd);
      }
    }
    if (dl.children.length > 0) sheet.appendChild(dl);
  }

  const actions = document.createElement("div");
  actions.className = "panel-actions";

  const dirBtn = document.createElement("button");
  dirBtn.type = "button";
  dirBtn.className = "btn-primary";
  dirBtn.textContent = "Directions";
  dirBtn.addEventListener("click", () => openDirections(t.lat, t.lon));
  actions.appendChild(dirBtn);

  const peeBtn = document.createElement("button");
  peeBtn.type = "button";
  peeBtn.className = "btn-primary visit-pee";
  peeBtn.innerHTML = "💧 I peed here";
  peeBtn.addEventListener("click", () => visit(1));
  actions.appendChild(peeBtn);

  const pooBtn = document.createElement("button");
  pooBtn.type = "button";
  pooBtn.className = "btn-secondary visit-poo";
  pooBtn.innerHTML = "💩 I pooped here";
  pooBtn.addEventListener("click", () => visit(2));
  actions.appendChild(pooBtn);

  sheet.appendChild(actions);

  ctx.panelRoot.appendChild(sheet);

  async function visit(vt: 1 | 2): Promise<void> {
    peeBtn.disabled = true;
    pooBtn.disabled = true;
    try {
      await ctx.api.markVisit(refForVisit(t), t.source, vt);
      ctx.notify(vt === 2 ? "Marked - 💩" : "Marked - 💧");
      ctx.onVisitedChange();
    } catch (err) {
      ctx.notify(friendlyMessage(err));
    } finally {
      peeBtn.disabled = false;
      pooBtn.disabled = false;
    }
  }
}
